import clsx from 'clsx';
import type { ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { FiCheck } from 'react-icons/fi';

import { Card } from '../Card/Card';
import { StatusPill } from '../StatusPill/StatusPill';

import s from './ChoiceCards.module.scss';

export interface Choice<T extends string = string> {
  id: T;
  title: string;
  desc: string;
  Icon: IconType;
  /** A small pill beside the title, e.g. for a path that is not fully ready yet. */
  badge?: string;
}

/**
 * One answer out of a few, each a card with an icon, a title and a line on what it means:
 * the opening question of a flow, where the words under the title are what helps you pick.
 * Two to a row, one to a row on a phone.
 *
 * @alias choice tiles, option cards, radio cards, selectable cards, intent cards
 */
export function ChoiceCards<T extends string>({
  items,
  selectedId,
  onSelect,
  footer,
}: {
  items: Choice<T>[];
  selectedId: T | null;
  onSelect: (id: T) => void;
  footer?: ReactNode;
}) {
  return (
    <>
      <div className={s.choiceGrid}>
        {items.map(({ id, title, desc, Icon, badge }) => (
          <Card
            key={id}
            padding="none"
            className={clsx(s.choiceTile, selectedId === id && s.choiceTileSelected)}
            onClick={() => onSelect(id)}
            ariaLabel={title}
            ariaPressed={selectedId === id}
          >
            <span className={s.choiceIcon} aria-hidden="true">
              <Icon />
            </span>
            <span className={s.choiceBody}>
              <span className={s.choiceTitle}>{title}</span>
              <span className={s.choiceDesc}>{desc}</span>
            </span>
            {(badge || selectedId === id) && (
              <span className={s.choiceCorner}>
                {badge && <StatusPill label={badge} tone="info" />}
                {selectedId === id && <FiCheck className={s.choiceCheck} aria-hidden="true" />}
              </span>
            )}
          </Card>
        ))}
      </div>
      {footer}
    </>
  );
}
